import ENV_CONFIG from '@config/environment/index';
import mixpanel from "mixpanel-browser";
import mixPanelConfig from "@utils/mixpanel";
import { getPubnubChannelName, handlePubnubData } from "@utils/common";
import Pubnub from "@utils/pubnub";
import Centrifugo from "@utils/centrifugo";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import store from "@src/store";
import get from "lodash/get";

const mixPanelLib = () => {
    if (mixpanel) {
        const initOptions = {
            debug: ENV_CONFIG.MIXPANEL.DEBUG,
            opt_out_tracking_by_default: false,
            ignore_dnt: true
        };
        if (ENV_CONFIG.MIXPANEL.API_HOST) {
            initOptions.api_host = ENV_CONFIG.MIXPANEL.API_HOST;
        }
        mixpanel.init(ENV_CONFIG.MIXPANEL.KEY, initOptions);
        // Register super properties (including PLATFORM) immediately so every event gets them,
        // including any fired before React mounts (e.g. early navigation / login flows).
        mixPanelConfig.setSuperProperties();
        // Do not call mixpanel.reset() here: it generates a new anonymous distinct_id on every
        // page load and breaks profile continuity until identify() runs again.
        // Reset is handled on logout via mixPanelConfig.reset().
    }
};

const getMoeDataCenter = () => (ENV_CONFIG.MOENGAGE && ENV_CONFIG.MOENGAGE.DATA_CENTER) || "dc_1";

const getMoengageSdkUrl = () =>
    `https://cdn.moengage.com/release/${getMoeDataCenter()}/versions/2/moe_webSdk.min.latest.js`;

const initializeMoengage = () => {
    if (!window) {
        return false;
    }
    if (
        window.Moengage &&
        typeof window.Moengage.add_user_attribute === "function" &&
        typeof window.Moengage.track_event === "function"
    ) {
        return true;
    }
    window.moeDataCenter = getMoeDataCenter();
    if (typeof window.moe !== "function") {
        return false;
    }
    try {
        const instance = window.moe({
            app_id: ENV_CONFIG.MOENGAGE.KEY,
            debug_logs: ENV_CONFIG.MOENGAGE.DEBUG,
        });
        if (instance) {
            window.Moengage = instance;
        }
        if (window.Moengage && typeof window.Moengage.call_web_push === "function") {
            window.Moengage.call_web_push();
        }
        if (
            window.Moengage &&
            typeof window.Moengage.add_user_attribute === "function" &&
            typeof window.Moengage.track_event === "function"
        ) {
            return true;
        }
        return false;
    } catch (error) {
        return false;
    }
};

const loadMoengageScript = () => {
    if (!window || !document) {
        return;
    }
    const sdkUrl = getMoengageSdkUrl();
    window.moeDataCenter = getMoeDataCenter();
    const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existingScript) {
        initializeMoengage();
        return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = sdkUrl;
    script.onload = () => {
        initializeMoengage();
    };
    document.head.appendChild(script);

    // Some browsers/extensions delay SDK global assignment even after load.
    let retries = 0;
    const maxRetries = 20;
    const retryTimer = setInterval(() => {
        retries += 1;
        if (initializeMoengage()) {
            clearInterval(retryTimer);
            return;
        }
        if (retries >= maxRetries) {
            clearInterval(retryTimer);
        }
    }, 500);
};

const moengage = () => {
    if (!window) {
        return;
    }
    if (initializeMoengage()) {
        return;
    }
    loadMoengageScript();
};

export const pubnub = (login = false) => {
    let channelName = getPubnubChannelName();
    if (channelName) {
        Pubnub(channelName, (data) => {
            handlePubnubData(data, 'from push');
        }, login);
    }
}

export const centrifugo = async (login = false) => {
    let channelName = getPubnubChannelName();
    if (channelName) {
        await Centrifugo(channelName, (data) => {
            handlePubnubData(data, 'from push');
        }, login);
    }
}

export const initializePubSub = async (login = false) => {
    // Check config for pubsub type
    const state = store.getState();
    const configResponse = get(state, 'headerDetails.configResponse');
    const pubsubType = get(configResponse, 'data.config.pubsub', 'pubnub').toLowerCase();
    
    if (pubsubType === 'centrifugo') {
        await centrifugo(login);
    } else {
        // Default to PubNub
        pubnub(login);
    }
}

export const firebaseAnalyticsInfo = () => {
    // Initialize Firebase
    const app = initializeApp(ENV_CONFIG.FIREBASE_CONFIG);
    getAnalytics(app);
}

export default () => {
    mixPanelLib();
    moengage();
    // Don't initialize PubSub here - it will be initialized after config is fetched
    // initializePubSub();
    firebaseAnalyticsInfo();
};
